import { ComplianceAudioBadge } from "@/contexts/ComplianceAudioBadge";
import {
  AudioLines,
  Download,
  Loader2,
  Mic,
  Pencil,
  RefreshCw,
  Sparkles,
  UserSquare2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { useFitBox } from "@/shared/hooks/useFitBox";
import { useQuotePreview } from "@/entities/pricing/model/useQuote";

import { toast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/ui/dialog";
import { Label } from "@/shared/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { pauseOtherAudio } from "@/shared/lib/exclusiveAudio";
import { cn } from "@/shared/lib/utils";
import { DH_LINE_ROUTE } from "@/entities/dh-profile/model/dhLines";
import {
  DH_DEFAULT_VOICE_LANGUAGE,
  DH_DEFAULT_VOICE_STYLE,
  DH_FEATURED_DIALECTS,
  DH_VOICE_DIALECTS,
  DH_VOICE_LANGUAGES,
  DH_VOICE_STYLES,
  dialectLabelOf,
  LEGACY_PROFILE_LOCK_TEXT,
  PRESET_VOICE_LOCK_TEXT,
  VOICE_STYLE_TRADEOFF_TEXT,
  voiceStyleLabelOf,
} from "@/entities/dh-profile/model/voiceV2Options";
import { voiceProfileSupportsTuning } from "@/entities/dh-profile/api/dhProfileService";
import { LegacyVoiceBadge } from "@/entities/dh-profile/ui/LegacyVoiceBadge";
import { digitalHumanService, type DhComposeOptions } from "../api/digitalHumanService";
import { useDhComposeEditor } from "./components/DhComposePanel";
import { DH_DEFAULT_VOICE, dhResolutionLabel } from "../model/dhCreateOptions";
import { replayQuoteParams } from "../model/dhCostEstimate";
import {
  currentPresetVoiceOf,
  inheritedTuningOf,
  REVOICE_SAME_HINT,
  revoiceSameAsCurrent,
} from "../model/revoiceGuard";
import { isRetiredDhMode } from "@/entities/dh-profile/model/dhLongform";
import { useDhPriceVariants } from "../model/useDhPriceVariants";
import { filmTypeOfMode } from "../model/dhRemake";
import { splitScriptLines } from "../model/dhScriptLines";
import { usePresetVoiceOptions } from "../model/usePresetVoiceOptions";
import { useDhTaskPolling } from "../model/useDhTaskPolling";
import { explainDhFailure } from "../model/dhFailureReason";

/** 成片下载 presign URL 过期(>30min)后 <video> 加载失败,再次重签的最小间隔(冷却,防坏产物 error 死循环)。 */
const RESIGN_COOLDOWN_MS = 30_000;

/**
 * 元数据到达前的兜底画幅 = 9:16。
 *
 * 后端 `DhAssetDto` 不回成片画幅(只有 id/status/url/kind),真比例只能等 `loadedmetadata`。
 * 兜底取**最窄**的一档:口播主力是竖版,真比例到手后只会变宽、不会溢出 Stage —— 反过来
 * 兜底给 16:9 的话,竖版成片一到就得往回缩,那正是 Content Jumping。
 */
const DH_FALLBACK_RATIO = 9 / 16;

/** E1 设置回显:档位 value → 展示名(与创建页 DH_MODE_OPTIONS 同口径;tts 单独档)。 */
const MODE_LABEL: Record<string, string> = {
  video: "真人换口播",
  flagship: "照片生动版",
  longform: "照片省流版",
  tts: "文生语音",
};

const STATUS_LABEL: Record<string, string> = {
  PENDING: "排队中",
  RUNNING: "生成中",
  SUCCESS: "已完成",
  PARTIAL: "部分完成",
  FAILED: "失败",
  BLOCKED: "已拦截",
  CANCELLED: "已取消",
};

/** 数字人任务详情:轮询状态;SUCCESS 播成片(presign 过期 onError 自动重签),FAILED/BLOCKED 显 errorMessage。 */
export default function DigitalHumanTaskDetailPage() {
  const { id } = useParams();
  const taskId = id ? Number(id) : null;
  const navigate = useNavigate();
  const { task, error, refresh } = useDhTaskPolling(taskId);

  // SUCCESS 后轮询已停,src 天然稳定(无需冻结)。但成片 presign 30min 过期,页面久留会导致 <video>
  // 加载 403 失败。onError → refresh() 重取详情(后端每次 get 重签 30min 新 URL)→ src 更新重载播放。
  // cooldown 防坏产物 error 事件把重签打成死循环(真过期约 30min 才触发一次)。
  /*
   * 0829 走查 P0:失败归因。原始 errorMessage 只进 console(排查要用),不进 DOM。
   * 依赖只有 errorMessage —— 同一条报文不会因为轮询重渲染而重复打日志。
   */
  const failure = useMemo(() => explainDhFailure(task?.errorMessage), [task?.errorMessage]);
  useEffect(() => {
    if (failure.raw && failure.raw !== failure.headline) {
      console.warn("[dh-task] 上游原始错误(未上屏):", failure.raw);
    }
  }, [failure]);

  const lastResignRef = useRef(0);
  const handleVideoError = () => {
    const now = Date.now();
    if (now - lastResignRef.current < RESIGN_COOLDOWN_MS) return;
    lastResignRef.current = now;
    refresh();
  };

  // ── Stage 取景(R2-a):盒子驱动,元素尺寸 == 画面尺寸 ───────────────────────────
  // 改前是 `max-h-[60vh] w-full`:高度封 60vh 对,宽度 w-full 撑满错 —— 竖版口播在 1440 上
  // 元素框 932×540 而画面只有 405×540(填充 43.5%,黑边 527px),1920 上更差(34.4%,926px)。
  // 现在宽度 = min(可用宽, 可用高 × 画面比例),视口越大画面越大但永不越屏,且没有黑边可留。
  const [videoRatio, setVideoRatio] = useState(DH_FALLBACK_RATIO);
  const fit = useFitBox(videoRatio);
  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const v = e.currentTarget;
    if (v.videoWidth > 0 && v.videoHeight > 0) setVideoRatio(v.videoWidth / v.videoHeight);
  };

  // B3 换音色重生成:复用本片形象+文案,换音色重跑 → 新任务。
  const [revoiceOpen, setRevoiceOpen] = useState(false);
  const [voiceKind, setVoiceKind] = useState<"preset" | "profile">("preset");
  const [presetVoice, setPresetVoice] = useState(DH_DEFAULT_VOICE);
  const [profileId, setProfileId] = useState("");
  // 声音 V2 三件套(2026-09-08):换音色也能带语言/方言/演绎。默认值在弹层打开时从
  // st(原任务设置)继承 —— 用户「只想换音色」时这三样随原任务不变(见 openRevoice)。
  const [rvLanguage, setRvLanguage] = useState<string>(DH_DEFAULT_VOICE_LANGUAGE);
  const [rvDialect, setRvDialect] = useState<string | null>(null);
  const [rvStyle, setRvStyle] = useState<string>(DH_DEFAULT_VOICE_STYLE);
  const [revoicing, setRevoicing] = useState(false);
  const voicesQuery = useQuery({
    queryKey: ["dh", "voice-profiles"],
    queryFn: () => digitalHumanService.listVoiceProfiles(),
    enabled: revoiceOpen && voiceKind === "profile",
  });
  const voiceProfiles = voicesQuery.data?.profiles ?? [];
  // ★1006 旧版声纹(supportsTuning===false):三件套不生效 → 与预置同样锁定。缺省按支持(不误锁)。
  const rvSelectedProfile =
    voiceKind === "profile" ? voiceProfiles.find((p) => String(p.id) === profileId) : undefined;
  const rvLegacyProfile = !!rvSelectedProfile && !voiceProfileSupportsTuning(rvSelectedProfile);
  const rvTuningUnlocked = voiceKind === "profile" && !rvLegacyProfile;
  // R3-a:预置音色与后端同源(回显查名 + 换音色弹窗共用一份)。
  const { options: presetVoiceOptions, labelOf: presetVoiceLabelOf } = usePresetVoiceOptions();

  const onRevoice = async () => {
    if (taskId == null) return;
    if (voiceKind === "profile" && !profileId) return;
    setRevoicing(true);
    try {
      // 三件套只在 profile 路带(与 createTask 同口径:预置音色是旧引擎,不塞噪声字段);
      // 方言仅 voiceLanguage=zh 可非空,这里再收一次口(后端同口径拦)。
      const body =
        voiceKind === "profile" && rvLegacyProfile
          ? // 旧版声纹:三件套不生效,不上送(锁定态下的值只是残影)。
            { voiceSource: "profile", voiceRef: profileId }
          : voiceKind === "profile"
            ? {
                voiceSource: "profile",
                voiceRef: profileId,
                voiceLanguage: rvLanguage,
                voiceDialect: rvLanguage === "zh" ? rvDialect : null,
                voiceStyle: rvStyle,
              }
            : { voiceSource: "preset", presetVoice };
      const { taskId: newId } = await digitalHumanService.revoiceTask(taskId, body);
      toast({ title: "已提交换音色", description: "新的口播视频生成中。" });
      setRevoiceOpen(false);
      navigate(`/ai-digital-human/tasks/${newId}`);
    } catch (e) {
      toast({
        title: "换音色失败",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setRevoicing(false);
    }
  };

  // 成片编辑(0909 字幕拖动+logo):零计费重合成 → 任务临时回 RUNNING,轮询自动接管刷新。
  const [composing, setComposing] = useState(false);
  const onCompose = async (options: DhComposeOptions) => {
    if (taskId == null) return;
    setComposing(true);
    try {
      await digitalHumanService.composeFinal(taskId, options);
      toast({ title: "已提交重合成", description: "约 1~2 分钟,完成后本页自动更新,不扣积分。" });
      refresh(); // 状态翻 RUNNING → 轮询 hook 恢复,回 SUCCESS 即新成片
    } catch (e) {
      toast({
        title: "重合成提交失败",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setComposing(false);
    }
  };

  // ── 成片编辑(0911 对齐视频线 D 版三列形制):useDhComposeEditor 要在「加载中」早返回
  //   **之前**调用,否则首帧(task 还没到)与次帧(task 到了)的 hook 数量不一样,React
  //   直接抛「Rendered more hooks than during the previous render」整页白屏(视频线
  //   useComposeEditor 头注同一个教训)。所以以下派生值一律按 task 可空写;真正渲染它们的
  //   分支都在早返回之后,值届时一定齐。
  const finalAsset =
    (task?.assets ?? []).find((a) => a.status === "SUCCESS" && a.url) ?? (task?.assets ?? [])[0];
  const active = task?.status === "PENDING" || task?.status === "RUNNING";
  // B5.5 文生语音:产物 kind=DH_AUDIO → 渲 audio 播放器(老后端无 kind 字段按视频渲,兼容)。
  const isAudioAsset = finalAsset?.kind === "DH_AUDIO";
  // E1 设置回显(老后端/老任务 settings=null → 整块不显,不编造)。
  const st = task?.settings ?? null;
  const scriptLines = splitScriptLines(st?.script);
  // Z2(0926 第八批):换音色这一单的价。api 按原单口径(档位 × 文案秒数)在服务端开正式单并绑定,
  //   这里用**同一组参数**问 dryRun —— 按钮和确认处的数就是结算扣的那个数。
  //   只在有入口时问(成片成功、非纯音频);估不出(老单无文案)→ null → 不出数字,不编。
  const dhVariants = useDhPriceVariants();
  // 1019:照片省流版已下架,api 对它的 revoice / retry 一律 400 → 换音色入口禁用并写明原因,不问价。
  const modeRetired = isRetiredDhMode(task?.mode);
  const revoiceQuote = useQuotePreview(
    task?.status === "SUCCESS" && !isAudioAsset && !modeRetired
      ? replayQuoteParams(task.mode, st?.resolution, st?.script, dhVariants)
      : null
  );
  const revoiceCredits = revoiceQuote.credits;

  const composeBase = task?.composeBase === true;
  const hadSubtitles = st?.burnSubtitles !== false;
  const subtitleSample = scriptLines[0] ?? null;
  const baseImageUrl = finalAsset?.thumbUrl ?? "";
  const composeEditor = useDhComposeEditor({
    composeBase,
    hadSubtitles,
    subtitleSample,
    baseImageUrl,
    applying: composing || active,
    onApply: onCompose,
  });
  // 成片编辑可用:与 0909 旧闸完全一致——真出了片的视频任务(纯音频无画面、活跃态成片
  // 未定,都不给)。
  const composeEditable =
    task?.status === "SUCCESS" && !!finalAsset?.url && !isAudioAsset && !!finalAsset?.thumbUrl;

  if (!task) {
    return (
      <div className="flex justify-center py-12">
        {error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : (
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        )}
      </div>
    );
  }

  const voiceEcho = st
    ? st.useOriginalVoice
      ? "原声(源视频克隆)"
      : st.voiceSource === "profile"
        ? "我的声纹"
        : st.presetVoice
          ? presetVoiceLabelOf(st.presetVoice)
          : st.voiceSource === "preset"
            ? "预置音色" // 老任务 payload 不存具体音色 id → 只说来源,不编造音色名
            : null
    : null;
  // R2-c 回工作台改这稿:只带**后端真回了的**字段。
  // ★带不回形象:`DhTaskSettings` 只有 avatarSource(preset/profile/upload_photo)这个**种类**,
  //   没有 avatarRef —— 具体是哪个预置主播、哪个我的形象,DTO 里根本没有;而 upload_photo 的
  //   key 本来就是一次性临时上传,过期即死链。所以形象一律回工作台重选,不拿一个会 404 的引用
  //   骗人(挂账:api 侧 DhSettings 补 avatarRef/voiceRef 后可整稿复原)。
  /**
   * ★失败态也要给这个出口(0813 琳琳:「字数太多点右边『返回』之后,没有修改的地方,
   *   只能重新进入」)。此前这颗按钮只挂在 SUCCESS 上,而**最需要改稿的恰恰是失败那次** ——
   *   「文案太长」这类失败,原样重来必然再失败一次;失败页却只有一个「返回」。
   * ★设置(`st`)与状态无关:它来自任务 payload,失败单一样有,所以带得回去。
   */
  const canRemake = ["SUCCESS", "FAILED", "BLOCKED"].includes(task.status) && !!st;
  const remakeLabel = task.status === "SUCCESS" ? "回工作台改这稿" : "改一改再出一次";
  const onRemake = () => {
    if (!st) return;
    // IA 收尾:直指本片所属的那条线(mode → 片型 → 线),不再让跳板替我们解析一遍。
    //   `filmTypeOfMode` 认不出的老 mode 落 talking,与跳板 `resolveDhLine` 的兜底同口径。
    const ft = filmTypeOfMode(task.mode ?? undefined);
    const line = ft === "photo" ? "photo" : ft === "tts" ? "tts" : "talking";
    navigate(DH_LINE_ROUTE[line], {
      state: {
        dhRemake: {
          fromTaskId: task.id,
          ...(task.mode ? { mode: task.mode } : {}),
          ...(st.script ? { script: st.script } : {}),
          ...(st.resolution ? { resolution: st.resolution } : {}),
          ...(st.speechRate != null ? { speechRate: st.speechRate } : {}),
          ...(st.presetVoice ? { presetVoice: st.presetVoice } : {}),
          ...(st.voiceSource ? { voiceSource: st.voiceSource } : {}),
          ...(st.performanceStyle ? { performanceStyle: st.performanceStyle } : {}),
          // 声音 V2 三件套(2026-09-08):交接包只带后端真回了的字段(老任务缺省不带);
          // voiceDialect 的 null=普通话是有意义的值,st 里显式有键才带。
          ...(st.voiceLanguage ? { voiceLanguage: st.voiceLanguage } : {}),
          ...(st.voiceDialect !== undefined ? { voiceDialect: st.voiceDialect } : {}),
          ...(st.voiceStyle ? { voiceStyle: st.voiceStyle } : {}),
        },
      },
    });
  };

  // 换音色弹层开阖:打开时把三件套默认值从原任务设置继承(词表验型,认不出的落默认)——
  // 用户「只想换音色」时语言/方言/演绎随原任务不变,不被悄悄改回默认。
  const openRevoice = (open: boolean) => {
    if (open) {
      // B2 #18:预置路初选 = **原单音色**(有的话),这样「一个都没改就确认」会被下面的同音色闸拦住;
      //   老单判不出原单音色 → 落默认(不拦,不锁死功能)。
      setPresetVoice(currentPresetVoiceOf(st) ?? DH_DEFAULT_VOICE);
      // ★1006:声音来源也继承原任务 —— 原单用声纹 → 默认「我的声纹」并预选原声纹;
      //   否则预置。预选原声纹后「没改」闸照样生效(同声纹 + 三件套没动 = 不许出片)。
      if (st?.voiceSource === "profile") {
        setVoiceKind("profile");
        setProfileId(st.voiceRef ? String(st.voiceRef) : "");
      } else {
        setVoiceKind("preset");
      }
      const inherited = inheritedTuningOf(st);
      setRvLanguage(inherited.language);
      setRvDialect(inherited.dialect);
      setRvStyle(inherited.style);
    }
    setRevoiceOpen(open);
  };

  // B2 #18(K4 实测没改音色也扣 840 出了一条一模一样的片):选中音色 == 原单音色 → 不许出片。
  const currentPresetVoice = currentPresetVoiceOf(st);
  const revoiceSame = revoiceSameAsCurrent(
    {
      voiceKind,
      presetVoice,
      profileId,
      tuning: rvTuningUnlocked
        ? { language: rvLanguage, dialect: rvDialect, style: rvStyle }
        : null,
    },
    st
  );

  const echoRows: { k: string; v: string }[] = st
    ? [
        { k: "档位", v: task.mode ? (MODE_LABEL[task.mode] ?? task.mode) : "" },
        // 清晰度只在照片档是真参数(视频档跟随源视频、tts 无画面),别的档不显
        {
          k: "清晰度",
          // B2 #12:回显档名(「高清 720」),不露契约值 480P —— 与创建页选择器同一份映射
          v:
            task.mode === "flagship" || task.mode === "longform"
              ? dhResolutionLabel(st.resolution)
              : "",
        },
        // 形象来源(2026-09-09 老板「结果页信息少」):DTO 早带 avatarSource,此前没渲染。
        // 只译种类不编名字(具体哪位预置主播/哪个形象,settings 只有 ref 没有名)。
        {
          k: "形象",
          v:
            st.avatarSource === "preset"
              ? "预置形象"
              : st.avatarSource === "profile"
                ? "我的素材"
                : st.avatarSource === "upload_photo"
                  ? "本次上传"
                  : "",
        },
        { k: "音色", v: voiceEcho ?? "" },
        // 声音 V2 三件套回显(2026-09-08):仅声纹路任务落这些键;老任务/预置任务
        // 值为空 → 行不显(同表内空值惯例,不编造「中文/普通话」)。口音行只在
        // 明确 voiceLanguage=zh 时显(voiceDialect=null 此时才有「普通话」语义)。
        {
          k: "语言",
          v: st.voiceLanguage
            ? (DH_VOICE_LANGUAGES.find((l) => l.value === st.voiceLanguage)?.label ??
              st.voiceLanguage)
            : "",
        },
        { k: "口音", v: st.voiceLanguage === "zh" ? dialectLabelOf(st.voiceDialect) : "" },
        { k: "演绎", v: voiceStyleLabelOf(st.voiceStyle) },
        { k: "语速", v: st.speechRate != null ? `${st.speechRate.toFixed(1)}x` : "" },
        // 字幕开关:0908 起只在用户显式关时存 false(开=不发键,默认权在服务端)——
        // 所以只有「关」值得说;null 不显,不替服务端编「开」。纯音频档无画面不显。
        { k: "字幕", v: st.burnSubtitles === false && !isAudioAsset ? "未烧录(本片已关)" : "" },
      ].filter((r) => r.v)
    : [];

  // ★播放器 / 各终态占位:tab 版(compose 可用)与无 tab 版共用同一份 —— 单源,理由同
  //   视频线 stagePlayer 头注(重排一度复制成两份,改一处漏一处不会报错,只是某些
  //   任务态下静默看不见)。
  const stagePlayer = (
    <CardContent className="flex min-h-0 flex-1 items-center justify-center p-4">
      {task.status === "SUCCESS" && finalAsset?.url && isAudioAsset ? (
        <div className="flex flex-col items-center gap-3" data-testid="dh-audio-player">
          {/* R3-c:emoji 不当图标(ui-ux-pro-max: no-emoji-icons)。
              /works 的音频占位 2026-07-27 就已经是 lucide AudioLines,这里跟上同一套。 */}
          <AudioLines className="h-10 w-10 text-primary" />
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio
            onPlay={pauseOtherAudio}
            src={finalAsset.url ?? undefined}
            controls
            onError={handleVideoError}
            className="w-full max-w-md"
          />
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ComplianceAudioBadge />
            纯音频产物(mp3)· 已埋 AIGC 合规标识
          </p>
        </div>
      ) : task.status === "SUCCESS" && finalAsset?.url ? (
        /* 取景框:必须有确定的宽**和**高,useFitBox 量的就是它(见该 hook 头注)。 */
        <div
          ref={fit.ref}
          data-testid="dh-stage"
          className="flex h-full min-h-0 w-full items-center justify-center"
        >
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            src={finalAsset.url ?? undefined}
            controls
            onError={handleVideoError}
            onLoadedMetadata={handleLoadedMetadata}
            data-dh-ratio={videoRatio.toFixed(4)}
            /* 宽高都显式给 px:`aspect-ratio` 在 jsdom 里会被丢掉(测不了),
               且它与 width/max-height 同时给时浏览器会让它让位,比例被拉坏。 */
            style={
              fit.width
                ? { width: fit.width, height: Math.round(fit.width / videoRatio) }
                : undefined
            }
            className="rounded-md bg-black object-contain"
          />
        </div>
      ) : active ? (
        <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>口播生成中,请稍候…成片会自动带合规标识。</span>
        </div>
      ) : (
        <p className="text-center text-sm text-muted-foreground">
          {task.status === "FAILED" || task.status === "BLOCKED" ? (
            /*
             * 0829 走查 P0:此前这里是 `task.errorMessage || 兜底` —— 后端分身侧**直接透传
             * 上游报文**,于是用户屏幕上是整段火山引擎 JSON(还带 Python 的 `b'` 前缀、
             * RequestId、内部 Action 名)。视频舱同位置之所以好看,是因为它后端翻好了人话。
             * 后端不在本批范围,所以归因落在前端:中文人话原样透传,机器报文按码归因,
             * 原文只进 console。
             */
            <span data-testid="dh-failure-note">
              {failure.headline}
              {failure.hint && <span className="mt-1 block text-xs">{failure.hint}</span>}
            </span>
          ) : (
            "暂无成片。"
          )}
        </p>
      )}
    </CardContent>
  );

  return (
    <div className="space-y-4 p-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserSquare2 className="h-5 w-5 text-primary" />
          {/* 0829 走查 P1:标题此前恒是「口播视频 #<19 位 id>」。任务自带 `name` 就用它。 */}
          <h1 className="truncate text-lg font-semibold">
            {task.name?.trim() || `${isAudioAsset ? "文生语音" : "口播视频"} #${task.id}`}
          </h1>
          {task.formulaName && (
            <span className="rounded-full border border-primary/40 bg-primary/5 px-2 py-0.5 text-2xs text-primary">
              本片套用:{task.formulaName}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* R2-c:播完了要有「然后呢」。改前详情页全部动作 = 返回/下载/换音色,
              想改一句文案重出得退回 hub 从头走一遍五步。
              ★只在**能真的复原**时才给这个按钮:settings=null 的老任务不给(见下方 canRemake)。 */}
          {canRemake && (
            <Button variant="outline" onClick={onRemake} data-testid="dh-remake">
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              {remakeLabel}
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate("/ai-digital-human")}>
            返回
          </Button>
        </div>
      </div>

      {/* ── Tool | Stage | Dock:三列工作台(2026-09-11 对齐视频线 D 版形制;老板反馈
          「成片编辑」整块太大、挂在视频下方,要跟 AI 视频详情页一致)──────────────────
          ★高度**只看视口**(`h-[calc(100dvh-11rem)]`),不跟兄弟节点抢:
            11rem ≈ 顶栏 56 + main padding 48 + 页头行 40 + gap 16。
            用固定公式而不是「flex 抢剩余高度」,理由同视觉舱:页头那个「本片套用 XXX」
            徽章是条件渲染的,抢剩余高度会让带徽章的任务把看片台压矮一截 —— 同一个任务
            在不同状态下看片台不该是不同大小。
          ★`min-h-[26rem]` 矮屏兜底:宁可让页面滚,也不把画面压没。
          ★compose 可用时三列 `212px_1fr_380px`(左工具 / 中画面 / 右 Dock),不可用时
            退回原有两列 —— 左栏整个不占位,不留空。<lg 时 DOM 顺序 = 画面 → 工具 → Dock
            (先看到片,再动它),用 order-* 把视觉顺序摆成 工具 → 画面 → Dock,与视频线
            VideoTaskDetailPage 的三列同一条规则。 */}
      <div className="lg:h-[calc(100dvh-11rem)] lg:min-h-[26rem]">
        <div
          className={cn(
            "grid h-full min-h-0 grid-cols-1 gap-4",
            composeEditable
              ? "lg:grid-cols-[212px_minmax(0,1fr)_380px]"
              : "lg:grid-cols-[minmax(0,1fr)_380px]"
          )}
        >
          {/* 画面(DOM 序第一 —— 先看到片) */}
          <Card data-testid="dh-stage-col" className="order-1 flex min-h-0 flex-col lg:order-2">
            {composeEditable ? (
              <Tabs defaultValue="play" className="flex min-h-0 flex-1 flex-col">
                <div className="border-b px-3 pt-2.5">
                  <TabsList>
                    <TabsTrigger value="play">播放</TabsTrigger>
                    <TabsTrigger value="layout">调版式</TabsTrigger>
                  </TabsList>
                </div>
                {/* ★`data-[state=inactive]:hidden` 不能省:Radix 把非当前页留成带 `hidden` 属性的空
                    tabpanel,而 `flex` 类会盖掉 `hidden` 的 display:none —— 空的「播放」页照样
                    flex-1 占走半截高度,把「调版式」画布挤到下半屏(1020 老板测试机实报)。 */}
                <TabsContent
                  value="play"
                  className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
                >
                  {stagePlayer}
                </TabsContent>
                <TabsContent value="layout" className="mt-0 min-h-0 flex-1 overflow-y-auto p-4">
                  {/* 同一块画布同时管 logo 与字幕 —— 拖完切回「播放」看的还是这一版。
                      竖版按视口高折算宽度,横版不受限:理由与视频线同一条(见
                      VideoTaskDetailPage 823-845 行头注),这里 videoRatio 是页面已有的
                      口播成片比例(useFitBox 同一份状态,onLoadedMetadata 里回填)。 */}
                  <div
                    className="mx-auto"
                    style={{ maxWidth: videoRatio > 1 ? "42rem" : "min(28rem, 33vh)" }}
                  >
                    {composeEditor.canvas}
                  </div>
                </TabsContent>
              </Tabs>
            ) : (
              stagePlayer
            )}
          </Card>

          {/* 工具栏(<lg 排在画面之后;高度锁骨架里与 Dock 同款自滚) */}
          {composeEditable && (
            <div data-testid="dh-tool-col" className="order-2 min-h-0 overflow-y-auto lg:order-1">
              <Card>
                <CardContent className="space-y-3 p-3">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                    <div className="min-w-0">
                      <h3 className="text-xs font-semibold leading-tight">成片编辑</h3>
                      <p className="text-2xs leading-tight text-muted-foreground">
                        秒级重出片,不花积分
                      </p>
                    </div>
                  </div>
                  {composeEditor.controls}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Dock:状态 + 操作 + 本次设置(自滚,不再跟着页面长;DOM/视觉恒最后) */}
          <div className="order-3 flex min-h-0 flex-col gap-4 overflow-y-auto">
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span>状态:{STATUS_LABEL[task.status] ?? "未知状态"}</span>
                  {task.status === "SUCCESS" && finalAsset?.url && (
                    <Button variant="ghost" size="sm" asChild>
                      {/* 用 downloadUrl(后端签了 attachment);老后端没有就回退 url。
                          不再靠 `download` 属性 —— 它对跨域地址无效,正是「点下载跳去播视频」的根。 */}
                      <a
                        href={finalAsset.downloadUrl || finalAsset.url}
                        download
                        rel="noreferrer"
                        data-testid="dh-download"
                      >
                        <Download className="mr-1 h-3 w-3" />
                        下载
                      </a>
                    </Button>
                  )}
                  {active && (
                    <Button variant="ghost" size="sm" onClick={refresh}>
                      <RefreshCw className="mr-1 h-3 w-3" />
                      刷新
                    </Button>
                  )}
                  {/* B5.5:tts 纯音频无形象,revoice 复原必失败 → 不给入口(重出直接再建一条更快) */}
                  {task.status === "SUCCESS" && !isAudioAsset && modeRetired && (
                    <span
                      className="inline-flex items-center gap-1 text-2xs text-muted-foreground"
                      data-testid="dh-revoice-retired"
                    >
                      <Button variant="ghost" size="sm" disabled>
                        <Mic className="mr-1 h-3 w-3" />
                        换音色重生成
                      </Button>
                      该档已下架
                    </span>
                  )}
                  {task.status === "SUCCESS" && !isAudioAsset && !modeRetired && (
                    <Dialog open={revoiceOpen} onOpenChange={openRevoice}>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <Mic className="mr-1 h-3 w-3" />
                          换音色重生成
                          {revoiceCredits != null && (
                            <span
                              className="ml-1 text-muted-foreground"
                              data-testid="dh-revoice-price"
                            >
                              · 约 {revoiceCredits.toLocaleString()} 积分
                            </span>
                          )}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>换音色重新生成</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                          <p className="text-xs text-muted-foreground">
                            复用本片的形象和文案,只换音色重新生成一条新视频
                            {revoiceCredits != null
                              ? `,本次预计 ≈ ${revoiceCredits.toLocaleString()} 积分(与首次出片同一价,按本片文案时长估)。`
                              : "(消耗积分,按实际时长结算)。"}
                          </p>
                          <div className="flex rounded-md border p-0.5">
                            <button
                              type="button"
                              onClick={() => setVoiceKind("preset")}
                              className={`flex-1 rounded px-2 py-1.5 text-xs transition ${
                                voiceKind === "preset"
                                  ? "bg-primary text-primary-foreground"
                                  : "text-muted-foreground hover:bg-muted"
                              }`}
                            >
                              预置音色
                            </button>
                            <button
                              type="button"
                              onClick={() => setVoiceKind("profile")}
                              className={`flex-1 rounded px-2 py-1.5 text-xs transition ${
                                voiceKind === "profile"
                                  ? "bg-primary text-primary-foreground"
                                  : "text-muted-foreground hover:bg-muted"
                              }`}
                            >
                              我的声纹
                            </button>
                          </div>
                          {voiceKind === "preset" ? (
                            /* B2 #18:预置音色**全部平铺**(此前下拉初态只见默认一项,其余要展开才看得到),
                               原单音色标「当前」—— 用户一眼知道自己在换哪一个。 */
                            <div
                              role="radiogroup"
                              aria-label="预置音色"
                              data-testid="dh-revoice-presets"
                              className="grid grid-cols-2 gap-1.5"
                            >
                              {presetVoiceOptions.map((o) => {
                                const on = presetVoice === o.value;
                                const isCurrent = currentPresetVoice === o.value;
                                return (
                                  <button
                                    key={o.value}
                                    type="button"
                                    role="radio"
                                    aria-checked={on}
                                    aria-label={o.label}
                                    data-testid={`dh-revoice-preset-${o.value}`}
                                    onClick={() => setPresetVoice(o.value)}
                                    className={cn(
                                      "flex items-center justify-between gap-1 rounded-md border px-2 py-1.5 text-left text-xs transition",
                                      on
                                        ? "border-primary bg-primary/5 font-medium text-primary"
                                        : "text-muted-foreground hover:border-muted-foreground/40 hover:bg-muted/40"
                                    )}
                                  >
                                    <span className="truncate">{o.label}</span>
                                    {isCurrent && (
                                      <span
                                        data-testid="dh-revoice-current"
                                        className="shrink-0 rounded bg-muted px-1 text-2xs text-muted-foreground"
                                      >
                                        当前
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          ) : voiceProfiles.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                              还没有声纹,先去「我的声纹」复刻本人声音。
                            </p>
                          ) : (
                            <div className="space-y-1.5">
                              <Label htmlFor="dh-revoice-profile" className="text-xs">
                                选择声纹
                              </Label>
                              {/* 0908 老板「弹框用系统默认」:换音色弹层的声纹下拉同批换
                                  shadcn(Radix SelectItem 不认空串 value → placeholder 走
                                  SelectValue,未选时 profileId 为空串保持既有禁用判定)。 */}
                              <Select
                                value={profileId || undefined}
                                onValueChange={(v) => setProfileId(v)}
                              >
                                <SelectTrigger
                                  id="dh-revoice-profile"
                                  aria-label="选择声纹"
                                  className="h-10 text-sm"
                                >
                                  <SelectValue placeholder="请选择我的声纹" />
                                </SelectTrigger>
                                <SelectContent>
                                  {voiceProfiles.map((p) => (
                                    <SelectItem key={p.id} value={String(p.id)}>
                                      <span className="inline-flex items-center gap-1.5">
                                        {p.label}
                                        {!voiceProfileSupportsTuning(p) && <LegacyVoiceBadge />}
                                      </span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {/* ── 声音 V2 三件套(2026-09-08 收尾,形制照创建页③卡的紧凑版):
                              语言/方言/演绎只对「我的声纹」真实生效(克隆声线走新模型);
                              预置音色是旧引擎 → 置灰 + 说明行(能力常驻可见,同创建页口径)。 */}
                          {!rvTuningUnlocked && (
                            <p
                              className="text-2xs leading-relaxed text-muted-foreground"
                              data-testid="dh-revoice-v2-locked"
                              data-lock-reason={rvLegacyProfile ? "legacy-profile" : "preset"}
                            >
                              {rvLegacyProfile ? LEGACY_PROFILE_LOCK_TEXT : PRESET_VOICE_LOCK_TEXT}
                            </p>
                          )}
                          <div
                            data-testid="dh-revoice-v2"
                            aria-disabled={!rvTuningUnlocked}
                            className={cn(
                              "space-y-3",
                              !rvTuningUnlocked && "pointer-events-none select-none opacity-50"
                            )}
                          >
                            <div className="space-y-1.5">
                              <Label htmlFor="dh-revoice-language" className="text-xs">
                                语言
                              </Label>
                              <Select
                                value={rvLanguage}
                                onValueChange={(v) => {
                                  setRvLanguage(v);
                                  // 契约「方言仅 zh 可非空」:换语言顺手收方言(同创建页)。
                                  if (v !== "zh") setRvDialect(null);
                                }}
                                disabled={!rvTuningUnlocked}
                              >
                                <SelectTrigger
                                  id="dh-revoice-language"
                                  data-testid="dh-revoice-language"
                                  className="h-8 text-sm"
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {DH_VOICE_LANGUAGES.map((l) => (
                                    <SelectItem key={l.value} value={l.value}>
                                      {l.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {rvLanguage === "zh" && (
                              <div className="space-y-1.5" data-testid="dh-revoice-dialect">
                                <Label className="text-xs">口音 / 方言</Label>
                                <div className="flex flex-wrap items-center gap-1.5">
                                  {[
                                    { value: null as string | null, label: "跟随原声" },
                                    ...DH_VOICE_DIALECTS.filter((d) =>
                                      (DH_FEATURED_DIALECTS as readonly string[]).includes(d.value)
                                    ),
                                  ].map((d) => {
                                    const dialectActive = rvDialect === d.value;
                                    return (
                                      <button
                                        key={d.value ?? "original"}
                                        type="button"
                                        aria-pressed={dialectActive}
                                        data-testid={`dh-revoice-dialect-${d.value ?? "original"}`}
                                        onClick={() => setRvDialect(d.value)}
                                        className={cn(
                                          "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                                          dialectActive
                                            ? "border-primary bg-primary/12 font-medium text-primary"
                                            : "border-border/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                                        )}
                                      >
                                        {d.label}
                                      </button>
                                    );
                                  })}
                                  {(() => {
                                    const more = DH_VOICE_DIALECTS.filter(
                                      (d) =>
                                        !(DH_FEATURED_DIALECTS as readonly string[]).includes(
                                          d.value
                                        )
                                    );
                                    const moreActive =
                                      rvDialect != null && more.some((d) => d.value === rvDialect);
                                    return (
                                      <Select
                                        value={moreActive ? rvDialect : ""}
                                        onValueChange={(v) =>
                                          setRvDialect(v === "__original__" ? null : v)
                                        }
                                        disabled={!rvTuningUnlocked}
                                      >
                                        <SelectTrigger
                                          aria-label="更多方言"
                                          data-testid="dh-revoice-dialect-more"
                                          className={cn(
                                            "h-[24px] w-auto gap-1 rounded-full px-2 py-0 text-xs transition-colors focus:ring-1 focus:ring-offset-0",
                                            moreActive
                                              ? "border-primary font-medium text-primary"
                                              : "border-border/60 text-muted-foreground"
                                          )}
                                        >
                                          <SelectValue placeholder="更多方言…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="__original__">回跟随原声</SelectItem>
                                          {more.map((d) => (
                                            <SelectItem key={d.value} value={d.value}>
                                              {d.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    );
                                  })()}
                                </div>
                              </div>
                            )}
                            <div className="space-y-1.5" data-testid="dh-revoice-style">
                              <Label className="text-xs">演绎方式</Label>
                              <div
                                role="radiogroup"
                                aria-label="演绎方式"
                                className="grid grid-cols-3 gap-1.5"
                              >
                                {DH_VOICE_STYLES.map((s) => {
                                  const styleActive = rvStyle === s.value;
                                  return (
                                    <button
                                      key={s.value}
                                      type="button"
                                      role="radio"
                                      aria-checked={styleActive}
                                      aria-label={s.label}
                                      data-testid={`dh-revoice-style-${s.value}`}
                                      onClick={() => setRvStyle(s.value)}
                                      className={cn(
                                        "rounded-md border px-2 py-1.5 text-center text-xs transition",
                                        styleActive
                                          ? "border-primary bg-primary/5 font-medium text-primary"
                                          : "text-muted-foreground hover:border-muted-foreground/40 hover:bg-muted/40"
                                      )}
                                    >
                                      {s.label}
                                    </button>
                                  );
                                })}
                              </div>
                              <p className="text-2xs text-muted-foreground">
                                {VOICE_STYLE_TRADEOFF_TEXT}
                              </p>
                            </div>
                          </div>
                        </div>
                        {revoiceSame && (
                          <p
                            data-testid="dh-revoice-same-hint"
                            className="text-2xs text-warning-foreground"
                          >
                            {REVOICE_SAME_HINT}
                          </p>
                        )}
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setRevoiceOpen(false)}>
                            取消
                          </Button>
                          <Button
                            disabled={
                              revoicing || (voiceKind === "profile" && !profileId) || revoiceSame
                            }
                            onClick={onRevoice}
                          >
                            {revoicing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            换音色出片
                            {revoiceCredits != null &&
                              ` · 约 ${revoiceCredits.toLocaleString()} 积分`}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* E1 本次设置(回显非预演,与视频/海报舱同范式):老任务 settings=null 整块不显。
              R2-b:台本从设置卡里那个 `max-h-40` 小框升成 Dock 的一个 tab —— 右栏本来就空着
              390px,没道理把正文压进小框里内滚。tab 命名与视频舱详情页同名同位。 */}
            {(echoRows.length > 0 || st?.script) && (
              <Tabs defaultValue="settings">
                <TabsList className="w-full">
                  <TabsTrigger value="settings" className="flex-1">
                    本次设置
                  </TabsTrigger>
                  {scriptLines.length > 0 && (
                    <TabsTrigger value="script" className="flex-1">
                      台本
                    </TabsTrigger>
                  )}
                </TabsList>

                <TabsContent value="settings" className="mt-3">
                  <Card data-testid="dh-settings-echo">
                    <CardContent className="space-y-2 p-4">
                      {echoRows.length > 0 ? (
                        <dl className="space-y-1">
                          {echoRows.map((r) => (
                            <div
                              key={r.k}
                              className="flex items-baseline justify-between gap-3 text-xs"
                            >
                              <dt className="shrink-0 text-muted-foreground">{r.k}</dt>
                              <dd className="min-w-0 truncate text-right">{r.v}</dd>
                            </div>
                          ))}
                        </dl>
                      ) : (
                        <p className="text-xs text-muted-foreground">这条任务没有留下设置记录。</p>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {scriptLines.length > 0 && (
                  <TabsContent value="script" className="mt-3">
                    <Card>
                      <CardContent className="space-y-2 p-4">
                        <ol className="space-y-1.5">
                          {scriptLines.map((line, i) => (
                            <li
                              key={i}
                              data-dh-script-line={i}
                              className="flex items-start gap-2 text-xs leading-relaxed"
                            >
                              <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-muted text-center font-mono leading-4 text-muted-foreground">
                                {i + 1}
                              </span>
                              <span className="min-w-0 flex-1 whitespace-pre-wrap">{line}</span>
                            </li>
                          ))}
                        </ol>
                        {/* 逐句时长后端不回 → 不做「念到哪句亮哪句」。估错一句就是指着 A 说 B。 */}
                        <p className="text-2xs text-muted-foreground">
                          成片按这份台本合成;要改文案就用上方「回工作台改这稿」。
                        </p>
                      </CardContent>
                    </Card>
                  </TabsContent>
                )}
              </Tabs>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
